import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useMemo } from "react";
import { Navigation, Scrollbar, A11y, Autoplay, Keyboard, } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { buttonHeights } from "../../button";
import { useMedia } from "../../utils/use-media";
// components
import { Indicator, LoopIndicator } from "../indicator";
// hooks
import { useIsMobile } from "./use-is-mobile";
// styles
import { StyledButton, CarouselContainer } from "./carousel.styles";
// constants
import { DEFAULT_RESPONSIVE_LAYOUT, DEFAULT_SLIDE_MARGIN, INITIAL_SLIDES_CONFIG, breakpoints, initSlides, } from "./constants";
const generateString = (length) => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    let result = "";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i += 1) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};
export const Carousel = (props) => {
    const { isMobile, isTablet } = useIsMobile();
    return _jsx(CarouselView, Object.assign({}, props, { isMobile: isMobile, isTablet: isTablet }));
};
export const CarouselView = ({ children, responsiveLayout, onChange, hideButtons = false, hideButtonsTablet = false, slidesWidth = [], slideMargin = DEFAULT_SLIDE_MARGIN, customButtonLeft, customButtonRight, previewId, startIndex, previewIdExtractor, autoSliding = false, loop = false, slidingInterval = 4, hideIndicator = false, prevEl, nextEl, isMobile, isTablet, id, }) => {
    const [initStepsToMove, slidesToShow] = useMedia(breakpoints, initSlides(Object.assign(Object.assign({}, DEFAULT_RESPONSIVE_LAYOUT), responsiveLayout)), INITIAL_SLIDES_CONFIG);
    const [activeSlide, setActiveSlide] = useState(startIndex);
    const classes = useRef({
        left: id ? `${id}-left` : generateString(12).toLowerCase(),
        right: id ? `${id}-right` : generateString(12).toLowerCase(),
        thumb: generateString(12).toLowerCase(),
        track: generateString(12).toLowerCase(),
    });
    const swiperRef = useRef();
    const shouldRenderControlButtons = useMemo(() => (!isMobile && !isTablet) ||
        (isMobile && !hideButtons) ||
        (isTablet && !hideButtonsTablet), [isMobile, isTablet, hideButtons, hideButtonsTablet]);
    const slidesCount = React.Children.count(children);
    const dragSize = (slidesToShow / slidesCount) * 100;
    const renderIndicator = () => {
        if (loop) {
            return (_jsx(LoopIndicator, { show: !hideIndicator, slidesCount: slidesCount, swiper: swiperRef.current, indicatorWidth: (1 / slidesCount) * 100, activeSlide: activeSlide || 0 }));
        }
        return _jsx(Indicator, { show: !hideIndicator, classes: classes.current });
    };
    return (_jsxs("div", Object.assign({ "data-preview-id": previewId }, { children: [_jsxs(CarouselContainer, Object.assign({ className: "carousel-container", slideMargin: slideMargin }, { children: [_jsx(StyledButton, { show: shouldRenderControlButtons && !customButtonLeft && !prevEl, className: `gdds-carousel__left-button ${classes.current.left}`, buttonType: "button", isIcon: true, height: { xsmall: buttonHeights.medium }, symbol: "ChevronLeft", stylingType: "flat", "data-testid": "carousel-button-left", slideMargin: slideMargin }), _jsx(Swiper, Object.assign({ modules: [A11y, Scrollbar, Navigation, Autoplay, Keyboard], onSwiper: (swiper) => {
                            window.swiper = swiper;
                            swiperRef.current = swiper;
                        }, touchRatio: initStepsToMove, observer: true, scrollbar: {
                            el: `.${classes.current.track}`,
                            draggable: true,
                            snapOnRelease: true,
                            dragClass: classes.current.thumb,
                            dragSize,
                        }, autoplay: autoSliding && {
                            delay: slidingInterval * 1000,
                            pauseOnMouseEnter: true,
                            disableOnInteraction: false,
                        }, keyboard: {
                            enabled: true,
                            onlyInViewport: true,
                        }, a11y: {
                            prevSlideMessage: "Previous slide",
                            nextSlideMessage: "Next slide",
                        }, onSlideChange: (s) => {
                            if (onChange)
                                onChange(s.realIndex);
                            setActiveSlide(s.realIndex);
                        }, slidesPerView: slidesWidth.length > 0 ? "auto" : slidesToShow, spaceBetween: slideMargin, initialSlide: startIndex, speed: 500, loop: loop, navigation: {
                            nextEl: nextEl || `.${classes.current.right}`,
                            prevEl: prevEl || `.${classes.current.left}`,
                        } }, { children: Array.isArray(children) ? (children.map((slide, index) => (_jsx(SwiperSlide, Object.assign({ "data-preview-id": typeof previewIdExtractor === "function"
                                ? previewIdExtractor(slide, index)
                                : undefined, style: { width: `${String(slidesWidth[index])}%` } }, { children: slide }), `slide-${String(index)}`)))) : (
                        // @ts-ignore
                        _jsx(SwiperSlide, Object.assign({ style: { width: "100%" } }, { children: children }))) })), _jsx(StyledButton, { className: `gdds-carousel__right-button ${classes.current.right}`, buttonType: "button", show: shouldRenderControlButtons && !customButtonRight && !nextEl, isIcon: true, height: { xsmall: buttonHeights.medium }, symbol: "ChevronRight", stylingType: "flat", "data-testid": "carousel-button-right", slideMargin: slideMargin })] })), renderIndicator()] })));
};
